/**
 * File Download Utility - Handles authenticated file downloads
 * Use this instead of window.open() for downloading files that require authentication
 */

import { getToken } from "dmsbasereact";
import { getBaseURL } from "../build_config";

/**
 * Downloads a file with authentication
 * @param {number} filePk - File primary key
 * @param {string} filename - Optional filename for download (defaults to "document-{filePk}")
 * @returns {Promise<void>}
 */
export const downloadFile = async (filePk, filename = null) => {
    try {
        const token = getToken();
        const url = `${getBaseURL()}/file/download?pk=${filePk}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token.access_token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Download failed: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename || `document-${filePk}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error("Error downloading file:", err);
        throw err; // Re-throw for caller to handle
    }
};

/**
 * Opens a file in a new tab with authentication (for viewing PDFs, images, etc.)
 * @param {number} filePk - File primary key
 * @returns {Promise<void>}
 */
export const viewFile = async (filePk) => {
    try {
        const token = getToken();
        const url = `${getBaseURL()}/file/${filePk}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token.access_token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to load file: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");

        // Clean up after a delay to allow the browser to open the URL
        setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
        }, 100);
    } catch (err) {
        console.error("Error viewing file:", err);
        throw err;
    }
};
